import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';
import HandleResponse from 'app/utils/HandleResponse';
import states from 'app/utils/States';
import SearchUsersAutoComplete from 'app/components/user/UserSearchAutocomplete';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

const initialGuestValues = {
    id: 0,
    email: "",
    phone_mobile: "",
    prefix: "",
    first: "",
    middle: "",
    last: "",
    suffix: "",
    address_one: "",
    address_two: "",
    city: "",
    state: "",
    zip: ""
}

export default function EditInvitationDialog(props) {

    const { open, onSave, onClose, onClosed, tickets } = props;

    const [ invitation, setInvitation ] = useState(null);
    const [ guestValues, setGuestValues ] = useState(null);
    const [ inviter, setInviter ] = useState({id:0}) // Inviter (group or user)
    const [ groups, setGroups ] = useState([]);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();
    const api = useApi();

    useEffect(() => {
        if (props.invitation) {
            setInvitation(props.invitation);
            setGuestValues(HandleResponse(props.invitation.guest, initialGuestValues));

            if (props.invitation.invitable) {
                setInviter(props.invitation.invitable);
            }
        }
    }, [props.invitation])

    const getGroups = () => api.get('/admin/group').then(res => setGroups(res.groups));

    useEffect(() => {
        getGroups();
    }, []);

    // Handler Functions ------------------------------------------

    const handleInvitation = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setInvitation({ ...invitation, [name]: value });
    };

    const handleGuestChange = name => e => {

        const target = e.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setGuestValues({ ...guestValues, [name]: value });
        setGuestUpdated(true);
    };

    const handleInviter = i => {
        setInviter(i);

        setInvitation({ ...invitation, invitable_type: 'App\\Models\\User' });
    }

    const handleGroupInviter = event => {
        console.log('hit')
        console.log(event)
        let group_id = event.target.value;
        let newInviter = groups.find(group => group.id === group_id);

        setInviter(newInviter);
        console.log(newInviter);
        setInvitation({ ...invitation, invitable_type: 'App\\Models\\Group' });
    }

    // Poster functions ------------------------------------------

    const postInvitation = () => {

        setLoading(true);

        const data = {
            ...invitation,
            ticket_id: invitation.ticket_id,
            guestValues: {...guestValues},
            inviter: {...inviter}
        }

        api.post(`/admin/invitation/${invitation.id}`, data)
            .then(() => onSave())
            .catch(error => {
                setLoading(false)
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    };

    // Error Handling -------------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    // Exting Functions ----------------------------------------

    const clearState = () => {
        setInvitation(null);
        setGuestValues(null);
    };

    const handleClosed = () => {
        //clearState();
        setLoading(false);
        if (onClosed) onClosed();
    }

    const initials = user => user.first.charAt(0) + user.last.charAt(0);

    return invitation && (
        <Dialog
            aria-labelledby="edit-date-dialog-title"
            open={open}
            aria-describedby="edit-date-dialogue-description"
            fullWidth
            maxWidth="sm"
            disableBackdropClick
            TransitionProps={{
                onExited: handleClosed,
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-date-dialog-title">
                Edit Invitation
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers id="edit-date-dialog-content">
                <form>
                    <Typography variant="body1" color="textSecondary" component="legend">
                        Invitation Details
                    </Typography>
                    <TextField
                        label="Title"
                        value={invitation.title}
                        onChange={handleInvitation('title')}
                        variant="outlined"
                        margin="normal"
                        fullWidth
                    />
                    <TextField
                        label="Message"
                        multiline
                        rowsMax="4"
                        value={invitation.message}
                        onChange={handleInvitation('message')}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                    />
                    <TextField
                        label="Invitation Details"
                        multiline
                        rowsMax="4"
                        value={invitation.details}
                        onChange={handleInvitation('details')}
                        variant="outlined"
                        fullWidth
                        helperText="Displays when QR code is scanned."
                        margin="normal"
                    />
                    <TextField
                        id="ticket-type"
                        label="Ticket Type"
                        select
                        fullWidth
                        value={invitation.ticket_id ?? 0}
                        onChange={handleInvitation('ticket_id')}
                        margin="normal"
                        variant="outlined"
                        >
                        <MenuItem value={0}>
                            No Ticket Selected
                        </MenuItem>
                        {tickets && tickets.map(ticket =>
                            <MenuItem key={ticket.id} value={ticket.id}>
                                {ticket.title} - ${ticket.price}
                            </MenuItem>
                        )}
                    </TextField>
                    <TextField
                        id="added-by"
                        label="Added By"
                        select
                        fullWidth
                        value={invitation.added_by}
                        onChange={handleInvitation('added_by')}
                        margin="normal"
                        variant="outlined"
                        >
                        <MenuItem value='user'>
                            User
                        </MenuItem>
                        <MenuItem value='group'>
                            Group
                        </MenuItem>
                    </TextField>
                    <Collapse in={invitation.added_by == 'user'}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Invited By User:
                        </Typography>
                        {(inviter.id > 0 && invitation.invitable_type === 'App\\Models\\User') ? (
                            <CardHeader
                                avatar={
                                    <Avatar
                                        aria-label={inviter.full_name}
                                        alt={inviter.full_name}
                                        src={inviter.avatar ? inviter.avatar.full_url : null}
                                        >
                                        {initials(inviter)}
                                    </Avatar>
                                }
                                title={inviter.full_name}
                                subheader={inviter.membership_title}
                                action={
                                    <IconButton
                                        aria-label="Remove User"
                                        onClick={() => handleInviter({id:0})}
                                        >
                                        <CloseIcon />
                                    </IconButton>
                                }
                            />
                        ) : (
                            <SearchUsersAutoComplete
                                closeOnSelect
                                setOnSelect
                                onSelect={handleInviter}
                                label="Find User"
                                admin
                            />
                        )}
                    </Collapse>
                    <Collapse in={invitation.added_by == 'group'}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Invited By Group:
                        </Typography>
                        <TextField
                            id="group-select"
                            select
                            value={invitation.invitable_type === 'App\\Models\\Group' ? inviter.id : 0}
                            onChange={event => handleGroupInviter(event)}
                            helperText={getErrorMessage('group_id')}
                            variant="outlined"
                            fullWidth
                            color="primary"
                            >
                            <MenuItem key={0} value={0}>
                                Select a Group
                            </MenuItem>
                            {groups.map(group => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.title}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Collapse>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                checked={invitation.flagged}
                                onChange={handleInvitation('flagged')}
                                value="flagged"
                            />
                        }
                        label="Has this invitation been flagged?"
                        margin="normal"
                        className={classes.marginBottom}
                    />
                    <Typography variant="body1" color="textSecondary" component="legend" >
                        Guest Details
                    </Typography>
                    <TextField
                        id="input-prefix"
                        label="Prefix"
                        value={guestValues.prefix}
                        onChange={handleGuestChange('prefix')}
                        fullWidth
                        error={isError('prefix')}
                        helperText={getErrorMessage('prefix')}
                        variant='outlined'
                        margin="normal"
                        required
                    />
                    <TextField
                        id="input-first"
                        label="First"
                        value={guestValues.first}
                        onChange={handleGuestChange('first')}
                        fullWidth
                        required
                        error={isError('first')}
                        helperText={getErrorMessage('first')}
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        id="input-middle"
                        label="Middle"
                        value={guestValues.middle}
                        onChange={handleGuestChange('middle')}
                        fullWidth
                        error={isError('middle')}
                        helperText={getErrorMessage('middle')}
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        id="input-last"
                        label="Last"
                        value={guestValues.last}
                        onChange={handleGuestChange('last')}
                        fullWidth
                        required
                        error={isError('last')}
                        helperText={getErrorMessage('last')}
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        id="input-suffix"
                        label="Suffix"
                        value={guestValues.suffix}
                        onChange={handleGuestChange('suffix')}
                        fullWidth
                        error={isError('suffix')}
                        helperText={getErrorMessage('suffix')}
                        margin="normal"
                        variant="outlined"
                        className={classes.marginBottom}
                    />
                    <Typography variant="body1" color="textSecondary">
                        Guest Home Address
                    </Typography>
                    <TextField
                        id="input-address-one"
                        label="Address One"
                        value={guestValues.address_one}
                        onChange={handleGuestChange('address_one')}
                        fullWidth
                        error={isError('address_one')}
                        helperText={getErrorMessage('address_one')}
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        id="input-address-two"
                        label="Address Two"
                        value={guestValues.address_two}
                        onChange={handleGuestChange('address_two')}
                        error={isError('address_two')}
                        helperText={getErrorMessage('address_two')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                    />
                    <TextField
                        id="input-city"
                        label="City"
                        value={guestValues.city}
                        onChange={handleGuestChange('city')}
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        error={isError('city')}
                        helperText={getErrorMessage('city')}
                    />
                    <Grid container spacing={3} className={classes.marginBottom}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                id="input-state"
                                select
                                label="State"
                                value={guestValues.state}
                                onChange={handleGuestChange('state')}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={isError('state')}
                                helperText={getErrorMessage('state')}
                                >
                                {states.map( state => {
                                    return (
                                        <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                    )
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                id="input-zip"
                                label="ZIP"
                                value={guestValues.zip}
                                onChange={handleGuestChange('zip')}
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                error={isError('zip')}
                                helperText={getErrorMessage('zip')}
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="body1" color="textSecondary">
                        Guest Contact Information
                    </Typography>
                    <TextField
                        id="input-email"
                        label="Email"
                        type="email"
                        autoComplete="email"
                        value={guestValues.email}
                        onChange={handleGuestChange('email')}
                        fullWidth
                        margin="normal"
                        error={isError('email')}
                        helperText={getErrorMessage('email')}
                        variant="outlined"
                    />
                    <TextField
                        id="input-mobile"
                        label="Mobile Phone"
                        value={guestValues.phone_mobile}
                        onChange={handleGuestChange('phone_mobile')}
                        fullWidth
                        margin="normal"
                        error={isError('phone_mobile')}
                        helperText={getErrorMessage('phone_mobile')}
                        variant="outlined"
                        className={classes.marginBottom}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={postInvitation} color="primary" disabled={loading} variant="contained">
                    Save Invitation
                </Button>
            </DialogActions>
        </Dialog>
    );
}
